import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSearchStore } from '../../../store/jpeTopPageStore'
import { CURRENCIES } from '../utils'

interface CurrencyPopoverProps {
  isOpen: boolean
  onClose: () => void
  buttonRef: React.RefObject<HTMLButtonElement | null>
}

export const CurrencyPopover: React.FC<CurrencyPopoverProps> = ({ isOpen, onClose, buttonRef }) => {
  const { selectedCurrency, setSelectedCurrency } = useSearchStore()
  const popoverRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  const handleSelectCurrency = (currencyCode: string) => {
    // Skip confirmation if selecting the same currency
    if (currencyCode === selectedCurrency) {
      onClose()
      return
    }

    if (currencyCode === 'JPY') {
      setSelectedCurrency(currencyCode)
      onClose()
      return
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      'Please be advised that currency conversions are provided for reference only. All payments will be processed in Japanese Yen (JPY).',
    )
    if (confirmed) {
      setSelectedCurrency(currencyCode)
      onClose()
    }
    // If cancelled, do nothing - popover stays open and currency unchanged
  }

  // Calculate position based on button position
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect()

      setPosition({
        top: buttonRect.bottom + 12,
        left: buttonRect.right - 280 + 42,
      })
    }
  }, [isOpen, buttonRef])

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const clickedButton = buttonRef.current?.contains(target)
      const clickedPopover = popoverRef.current?.contains(target)

      if (!clickedButton && !clickedPopover) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose, buttonRef])

  if (!isOpen) return null

  // Portal content
  const popoverContent = (
    <div
      ref={popoverRef}
      className="bg-white rounded-lg shadow-lg border border-gray-200 py-2"
      style={{
        position: 'fixed',
        width: '280px',
        top: `${position.top}px`,
        left: `${position.left}px`,
        maxHeight: '400px',
        borderRadius: '15px',
        overflowY: 'auto',
        zIndex: 999999,
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
      }}
    >
      <div className="px-4 py-2">
        <h3 className="font-size-10 font-normal text-gray-700">Switch the display currency</h3>
      </div>
      <div className="py-1">
        {CURRENCIES.map((currency) => (
          <button
            key={currency.code}
            onClick={() => handleSelectCurrency(currency.code)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
          >
            <span className="text-sm text-gray-900">
              {currency.code} — {currency.name}
            </span>
            {selectedCurrency === currency.code && (
              <span className="text-gray-900">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M16.6667 5L7.50004 14.1667L3.33337 10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  )

  // Render using Portal to escape parent stacking context
  return createPortal(popoverContent, document.body)
}
